import { Alert } from 'components/Alert';
import { GooglePlacesInput } from 'components/profile/AddressValidation/AddressValidation';
import Box from 'components/UI/Layout/Box';
import SpinnerModal from 'components/UI/Spinner/SpinnerModal';
import { Formik } from 'formik';
import React, { useState } from 'react';
import { Color } from 'modules/styles/colors';
import { FontSize } from 'modules/styles/variables';
import { AddressComponent, GeoCodeLocation } from 'services/LocationService/types';
import { Address } from 'store/preRegistration/types';
import StyledButton from 'components/UI/Button/StyledButton';
import { ContactCancelButton } from 'components/styled';

import { MuiBox, MuiContainer, MuiTextField, MuiTypography } from 'theme/material-ui';
import { oc } from 'ts-optchain';
import { Dialog } from '@material-ui/core';

export interface Props {
  homeAddress: Address;
  onClose: () => void;
  open: boolean;
  handleSubmit: (address: Address) => void;
}

interface FieldValue {
  name: string;
  value: string;
}

function addressFromComponents(addressComponents: AddressComponent[]) {
  const address1 = {
    number: '',
    street: ''
  };

  const newAC: FieldValue[] = [];

  for (const ac of addressComponents) {
    switch (ac.types[0]) {
      case 'street_number':
        address1.number = ac.long_name;
        break;
      case 'route':
        address1.street = ac.long_name;
        break;
      case 'locality':
        newAC.push({ name: 'city', value: ac.long_name });
        break;
      case 'administrative_area_level_1':
        newAC.push({ name: 'state', value: ac.short_name });
        break;
      case 'country':
        newAC.push({ name: 'country', value: ac.long_name });
        break;
      case 'zip':
        newAC.push({ name: 'zip', value: ac.long_name });
        break;
      default:
    }
  }

  newAC.push({ name: 'address1', value: `${address1.number} ${address1.street}` });

  return newAC;
}

export function EditAddressScreen({ homeAddress, onClose, open, handleSubmit }: Props) {
  const [isSubmitting] = useState(false);
  const initialValues: Address = {
    address1: oc(homeAddress).address1(''),
    address2: oc(homeAddress).address2(''),
    state: oc(homeAddress).state(''),
    city: oc(homeAddress).city(''),
    country: oc(homeAddress).country(''),
    zip: oc(homeAddress).zip('')
  };

  const handleValidate = (values: Address) => {
    return values.address1.length === 0 ? { address1: 'Please enter a valid address' } : {};
  };

  const updateAddress = (details: GeoCodeLocation) => {
    return addressFromComponents(details.address_components);
  };

  const onSubmit = async (values: Address) => {
    if (values.address1 && values.city && values.state && values.zip) {
      Alert.alert(
        'Address Verified',
        'The address has been verified but you will need to click confirm at the bottom of the page in order to save it to your profile.'
      );
      handleSubmit(values);
      onClose();
    }
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      <MuiBox mt={6}>
        <SpinnerModal isLoading={isSubmitting} />
        <MuiContainer maxWidth="sm">
          <Formik initialValues={initialValues} validate={handleValidate} onSubmit={onSubmit}>
            {({ values, handleChange, setFieldValue, isValid, handleSubmit }) => (
              <MuiBox p={3}>
                <MuiBox mb={2}>
                  <MuiTypography
                    fontWeight={600}
                    fontSize={FontSize.large}
                    color={Color.textLight}
                    gutterBottom
                  >
                    Edit Address
                  </MuiTypography>
                </MuiBox>

                <MuiBox mb={2}>
                  <GooglePlacesInput
                    handleOnPress={(geoCode: GeoCodeLocation) => {
                      const addressFieldValues = updateAddress(geoCode);

                      addressFieldValues.forEach(ac => {
                        setFieldValue(ac.name, ac.value);
                      });
                    }}
                  />

                  <MuiTextField
                    fullWidth
                    required
                    name="address1"
                    label="Address Line 1"
                    value={values.address1}
                    onChange={handleChange('address1')}
                  />

                  <Box vSpacingBottom={10} />

                  <MuiTextField
                    fullWidth
                    name="address2"
                    label="Address Line 2"
                    value={values.address2}
                    onChange={handleChange('address2')}
                  />

                  <Box vSpacingBottom={10} />

                  <MuiTextField
                    fullWidth
                    required
                    name="city"
                    label="City"
                    value={values.city}
                    onChange={handleChange('city')}
                  />

                  <Box vSpacingBottom={10} />

                  <MuiTextField
                    fullWidth
                    required
                    name="state"
                    label="State"
                    value={values.state}
                    onChange={handleChange('state')}
                  />

                  <Box vSpacingBottom={10} />

                  <MuiTextField
                    fullWidth
                    required
                    label="Postal Code"
                    value={values.zip}
                    onChange={handleChange('zip')}
                  />
                </MuiBox>

                <MuiBox mb={1}>
                  <ContactCancelButton fullWidth disabled={!isValid} onClick={handleClose}>
                    Cancel
                  </ContactCancelButton>
                </MuiBox>

                <StyledButton fullWidth disabled={!isValid} onClick={handleSubmit}>
                  Submit
                </StyledButton>
              </MuiBox>
            )}
          </Formik>
        </MuiContainer>
      </MuiBox>
    </Dialog>
  );
}
